import React from 'react';
import { IAuditoriaCorrelacaoFiscalResultado } from '../interfaces/IAuditoriaCorrelacaoFiscal';

interface Props {
  resultado: IAuditoriaCorrelacaoFiscalResultado;
  onVerDetalhe: () => void;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (value?: number) =>
  `${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

// COR-261: teto de severidade agora é ALERTA — a auditoria nunca mais classifica ERRO.
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const AuditoriaResumoCorrelacaoFiscal: React.FC<Props> = ({ resultado, onVerDetalhe }) => {
  if (resultado.status === 'CONCILIADO' && resultado.itens.length === 0) return null;

  const top5 = [...resultado.itens]
    .sort((a, b) => b.percentualDesvio - a.percentualDesvio)
    .slice(0, 5);

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Correlação Receita × Impostos (PIS/COFINS/ICMS/ISS)
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            Lei 9.430/96 Art. 3º · LC 87/96 ICMS · LC 116/03 ISS · Lei 9.430/96 Art. 44 I
          </span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-light-primary' onClick={onVerDetalhe}>
            Ver Detalhes
          </button>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='row mb-5'>
          <div className='col-3 text-center border-end'>
            <div className='fs-2 fw-bolder text-warning'>{resultado.totalAlertas}</div>
            <div className='text-muted fs-8'>Alertas (&gt; 5%)</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-2 fw-bolder text-warning'>{resultado.totalAvisos}</div>
            <div className='text-muted fs-8'>Avisos (≤ 5%)</div>
          </div>
          <div className='col-3 text-center border-end'>
            <div className='fs-2 fw-bolder text-success'>
              {formatCurrency(resultado.passivoTributarioTotal)}
            </div>
            <div className='text-muted fs-8'>Impacto Fiscal Confirmado</div>
          </div>
          <div className='col-3 text-center'>
            <div className='fs-2 fw-bolder'>
              {formatCurrency(resultado.passivoReferenciaTotal)}
            </div>
            <div className='text-muted fs-8'>Referência Informativa</div>
          </div>
        </div>

        {/* COR-261: uma variação estatística da alíquota efetiva não é, por si só, evidência de
            infração fiscal — a auditoria nunca conclui ERRO nem confirma passivo automaticamente.
            A referência informativa (COR-262) é calculada só tributo a tributo, com evidência
            individual objetiva, nunca a partir do desvio agregado da AE total. */}
        {resultado.passivoReferenciaTotal > 0 && (
          <div className='alert alert-light-primary border border-primary p-3 fs-8 mb-5'>
            <strong>Referência informativa (não confirmada):</strong>{' '}
            {formatCurrency(resultado.passivoReferenciaTotal)} — pendente de validação documental
            do tributo, competência, município, serviço e natureza da receita.
          </div>
        )}

        {top5.length > 0 && (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th className='text-center'>Mês/Ano</th>
                  <th className='text-end'>Receita</th>
                  <th className='text-center'>AE Mensal</th>
                  <th className='text-center'>AE Média</th>
                  <th className='text-center'>Desvio</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {top5.map((item, i) => (
                  <tr key={i}>
                    <td>
                      <span className='fw-bold'>{item.empresaNome}</span>
                      <br />
                      <span className='text-muted fs-8'>{item.cnpj}</span>
                    </td>
                    <td className='text-center fw-bold'>{item.mesAno}</td>
                    <td className='text-end fw-bold'>{formatCurrency(item.receitaBruta)}</td>
                    <td className='text-center'>{formatPct(item.aliquotaEfetiva)}</td>
                    <td className='text-center text-muted'>{formatPct(item.aliquotaMediaMovel)}</td>
                    <td className='text-center fw-bolder text-danger'>{formatPct(item.percentualDesvio)}</td>
                    <td className='text-center'><StatusBadge status={item.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaResumoCorrelacaoFiscal;
